import { KeySignature } from '../../types';

// Note to frequency mapping (A4 = 440Hz)
export const NOTES: { [key: string]: number } = {
  'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3, 'E': 4, 'F': 5,
  'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8, 'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11,
};

export const noteToFrequency = (note: string, octave: number): number => {
  const semitonesFromA4 = (octave - 4) * 12 + (NOTES[note] - NOTES['A']);
  return 440 * Math.pow(2, semitonesFromA4 / 12);
};

export const midiToFrequency = (midiNote: number): number => {
  return 440 * Math.pow(2, (midiNote - 69) / 12);
};

export const SHARP_NOTES = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
export const FLAT_NOTES = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B'];

// Flat preference mapping - converts sharp notes to flat equivalents where musically preferred.
// Convention: F# and C# are preferred over Gb/Db (no conversion). Bb, Eb, Ab are preferred over A#/D#/G# (converted).
const FLAT_PREFERENCE_MAP: { [sharp: string]: string } = {
  // C# is intentionally NOT mapped to Db — C# is the canonical spelling
  'D#': 'Eb',
  // F# is intentionally NOT mapped to Gb — F# is the canonical spelling
  'G#': 'Ab',
  'A#': 'Bb'
};

// Apply flat preference to chord notation (A# -> Bb, D# -> Eb, etc.)
// Also normalizes Unicode accidentals (♯ -> #, ♭ -> b) for consistent storage.
export const applyFlatPreference = (chordString: string): string => {
  if (!chordString) return chordString;
  
  // Apply flat preference to main chord root and bass notes
  let result = chordString;
  
  // Replace sharp notation with flat preference
  for (const [sharp, flat] of Object.entries(FLAT_PREFERENCE_MAP)) {
    // Match sharp note at start of chord or after slash (handles both # and ♯)
    const sharpRegex = new RegExp(`\\b${sharp.replace('#', '[#♯]')}\\b`, 'g');
    result = result.replace(sharpRegex, flat);
  }
  
  // Normalize any remaining Unicode accidentals to ASCII for consistent storage
  result = result.replace(/♯/g, '#').replace(/♭/g, 'b');
  
  return result;
};

// Canonical Chord Notator storage format.
// Keeps Unicode accidentals and preferred diminished symbols for newly written charts.
export const formatChordNotatorChord = (chordString: string): string => {
  if (!chordString) return chordString;

  let result = chordString.trim();

  // Strip spurious space between root+accidental and quality (e.g. "Eb 7sus" → "Eb7sus").
  result = result.replace(/^([A-G][♯♭#b]?)\s+/, '$1');

  // Normalize dash-minor notation used by some lead sheets (e.g. A-7, C-maj7).
  result = result.replace(/^([A-G][#b♯♭]?)-(.*)$/, (_match, root: string, rest: string) => {
    if (!rest) return `${root}m`;
    if (rest.startsWith('maj7')) return `${root}m(maj7)`;
    return `${root}m${rest}`;
  });

  // Prefer the palette symbols for diminished chords.
  result = result.replace(/dim7/g, '°7');
  result = result.replace(/dim(?![a-zA-Z])/g, '°');

  // Prefer Unicode sharp in alterations and roots.
  result = result.replace(/#/g, '♯');

  // Normalize slash spacing for readability while preserving 6/9.
  if (result.includes('/')) {
    result = result.replace(/\s*\/\s*/g, ' / ');
    result = result.replace(/6\s*\/\s*9/g, '6/9');
  }

  // Keep Unicode accidentals in chart storage for note roots and bass notes.
  result = result
    .split('/')
    .map((part, index) => {
      // Preserve slash extensions like 6/9 untouched.
      if (index > 0 && /^\d+$/.test(part)) {
        return part;
      }

      return part.replace(/^([A-G])([#b]?)/, (_match, note: string, accidental: string) => {
        const accidentalSymbol = accidental === '#' ? '♯' : accidental === 'b' ? '♭' : '';
        return `${note}${accidentalSymbol}`;
      });
    })
    .join('/');

  return result;
};

export const KEY_PREFERENCES: Record<KeySignature, 'sharps' | 'flats'> = {
  'C': 'sharps', 'G': 'sharps', 'D': 'sharps', 'A': 'sharps', 'E': 'sharps', 'B': 'sharps', 'F#': 'sharps', 'C#': 'sharps',
  'F': 'flats', 'Bb': 'flats', 'Eb': 'flats', 'Ab': 'flats', 'Db': 'flats', 'Gb': 'flats', 'Cb': 'flats',
};

export const transposeChord = (chordString: string, semitones: number, key: KeySignature): string => {
  if (!chordString || semitones === 0) return chordString;

  const transposeNote = (noteString: string): string => {
    const sanitized = noteString.replace(/♯/g, '#').replace(/♭/g, 'b');
    const rootMatch = sanitized.match(/^[A-G][b#]?/);
    if (!rootMatch) return noteString;
    
    const root = rootMatch[0];
    const quality = sanitized.substring(root.length);
    
    const rootIndex = NOTES[root];
    if (rootIndex === undefined) return noteString;

    const transposedIndex = (rootIndex + semitones % 12 + 12) % 12;
    
    const preference = KEY_PREFERENCES[key];
    const noteNameMap = preference === 'flats' ? FLAT_NOTES : SHARP_NOTES;
    const newRoot = noteNameMap[transposedIndex];

    return newRoot + quality;
  };

  return chordString.split('/').map(transposeNote).join('/');
};
